import { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import Layout from '../components/Layout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { red } from '@mui/material/colors';
import { LoadingButton } from '@mui/lab';

// Roles (include DB default 'staff')
const ROLE_OPTIONS = [
  'staff_level_1',
  'staff_level_2',
  // 'Engineer',
  // 'Sales',
  // 'Surveyor',
  // 'Accountant',
  'admin',
  // 'System Admin',
];

const ROLE_LABELS = {
  staff_level_1: '一級員工',
  staff_level_2: '二級員工',
  admin: '管理員',
};

export default function Users() {
  const user_id = localStorage.getItem('user_id');
  const role = localStorage.getItem("role");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState('');
  const [submittingUser, setSubmittingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  const [form, setForm] = useState({
    username: '',
    email: '',
    role: 'staff_level_1',
    remark: '',
    password: '', // only required for create or password reset
  });

  const canDelete = useMemo(() => user_id && Number(user_id) <= 3, [user_id]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    const params = {};
    if (searchText) params.search = searchText;
    await runFetchUsers(params);
  };

  const runFetchUsers = async (params = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/users', { params: params });
      const formatted = (res.data || []).map((u) => ({
        ...u,
        id: u.id,
        created_at: u.created_at ? dayjs(u.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
        updated_at: u.updated_at ? dayjs(u.updated_at).format('YYYY-MM-DD HH:mm:ss') : '',
      }));
      setRows(formatted);
    } catch (e) {
      setError('無法載入使用者清單');
    } finally {
      setLoading(false);
    }
  };

  const resetFilter = () => {
    setSearchText('');
    runFetchUsers({});
  };

  const handleOpen = () => {
    setEditingId(null);
    setForm({ username: '', email: '', role: 'staff_level_1', remark: '', password: '' });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ username: '', email: '', role: 'staff_level_1', remark: '', password: '' });
    setError('');
  };

  const handleEdit = (row) => {
    setForm({
      username: row.username || '',
      email: row.email || '',
      role: row.role || 'staff_level_1',
      remark: row.remark || '',
      password: '',
    });
    setEditingId(row.id);
    setOpen(true);
  };

  const simpleEmailValid = (e) => !!e && e.includes('@') && e.includes('.');

  const validate = () => {
    if (!form.username.trim()) return '請輸入使用者帳號 (username)';
    if (!form.email) return '請輸入電郵';
    if (form.email && !simpleEmailValid(form.email.trim())) return '電郵格式不正確';
    if (!ROLE_OPTIONS.includes(form.role)) return '請選擇有效角色';
    if (!editingId && !form.password.trim()) return '請輸入密碼';
    return '';
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }

    try {
      setLoading(true);
      setSubmittingUser(true);
      setError('');
      if (editingId) {
        const { username, email, role, remark, password } = form;
        const payload = { username, email, role, remark };
        if (password) payload.password = password; // optional reset
        await axios.put(`/users/${editingId}`, payload);
      } else {
        const { username, email, role, remark, password } = form;
        await axios.post('/users', { username, email, role, remark, password });
      }
      handleClose();
      await fetchUsers();
    } catch (e) {
      console.log("e", e)
      if (e.response.data.message === "Username already exists") {
        setError('使用者帳戶已註冊');
      } else if (e.response.data.message === "Email already exists") {
        setError('使用者電郵已註冊');
      } else {
        setError('提交使用者資料失敗');
      }

    } finally {
      setLoading(false);
      setSubmittingUser(false);
    }
  };

  const handleDelete = (id) => setConfirmDeleteId(id);

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setLoading(true);
      setDeletingUser(true);
      await axios.put(`/users/${confirmDeleteId}`, { active: false }); // soft delete
      setConfirmDeleteId(null);
      await fetchUsers();
    } catch (e) {
      setError('刪除使用者失敗');
    } finally {
      setLoading(false);
      setDeletingUser(false);
    }
  };

  const columns = [
    { field: 'username', headerName: '帳號', width: 180, minWidth: 160, maxWidth: 260 },
    { field: 'email', headerName: '電郵', width: 240, minWidth: 220, maxWidth: 360 },
    // { field: 'role', headerName: '角色', width: 160, minWidth: 140, maxWidth: 220 },
    { field: 'remark', headerName: '備註', width: 320, minWidth: 260, maxWidth: 600 },
    { field: 'created_at', headerName: '新增時間', width: 180, minWidth: 150, maxWidth: 220 },
    { field: 'updated_at', headerName: '最後修改時間', width: 180, minWidth: 150, maxWidth: 220 },
    {
      field: 'actions',
      headerName: '動作',
      width: 140,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params.row)}><EditIcon /></IconButton>
          {canDelete && (
            <IconButton onClick={() => handleDelete(params.row.id)} sx={{ color: red[800] }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  return (
    <Layout>
      <Typography variant="h5" mb={2} sx={{ paddingLeft: '8px' }}>使用者管理</Typography>

      <Box className="new-item-button-container" sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, paddingLeft: '8px' }}>
        <Button className="new-item-button" variant="contained" onClick={handleOpen}>新增使用者</Button>
      </Box>

      <Box className="filter-container" sx={{ display: 'flex', gap: 1, paddingLeft: '8px', mb: 2, alignItems: 'center' }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="搜尋帳號、電郵或備註"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button variant="contained" onClick={fetchUsers}>搜尋</Button>
        <Button variant="contained" onClick={resetFilter}>重設</Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnMenu={true}
          />
        </Box>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? '修改使用者' : '新增使用者'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="帳號 (username)"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label="電郵"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="dense"
          />
          {role === "admin" &&
            <FormControl fullWidth margin="dense">
              <InputLabel id="role-label">角色</InputLabel>
              <Select
                labelId="role-label"
                label="角色"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>{ROLE_LABELS[r] || r}</MenuItem>
                ))}
              </Select>
              <FormHelperText>選擇最小必要權限</FormHelperText>
            </FormControl>
          }
          {/* Password only required for create; optional for edit to reset */}
          <TextField
            fullWidth
            type="password"
            label={editingId ? '重設密碼（可選）' : '密碼'}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            margin="dense"
          />

          <TextField
            fullWidth
            label="備註"
            value={form.remark}
            onChange={(e) => setForm({ ...form, remark: e.target.value })}
            margin="dense"
            multiline
            minRows={2}
          />
          {error && (
            <Typography variant="body2" color="error" sx={{ ml: 1 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={handleClose}>取消</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? '更新' : '建立'}
          </Button> */}
          <Button onClick={handleClose} disabled={submittingUser}>取消</Button>
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            loading={submittingUser}
          >
            {editingId ? '更新' : '建立'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>您確定要刪除此使用者嗎？</DialogContent>
        <DialogActions>
          {/* <Button onClick={() => setConfirmDeleteId(null)}>取消</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">刪除</Button> */}
          <Button onClick={() => setConfirmDeleteId(null)} disabled={deletingUser}>取消</Button>
          <LoadingButton
            onClick={confirmDelete}
            color="error"
            variant="contained"
            loading={deletingUser}
          >
            刪除
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
